<template>
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content" v-if="point">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-wifi me-2"></i>{{ point.name }}
          </h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <div class="row">
            <!-- Connection Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-body">
                  <h6 class="card-title text-primary mb-3">
                    <i class="mdi mdi-router-network me-1"></i> Connection Details
                  </h6>

                  <div class="mb-3">
                    <label class="text-muted small">Connection Type</label>
                    <div class="d-flex align-items-center">
                      <img :src="getConnectionIcon(point.connectionType)" width="30" class="me-2">
                      <h5 class="mb-0">{{ point.connectionType }}</h5>
                    </div>
                  </div>

                  <div class="mb-3">
                    <label class="text-muted small">Speed</label>
                    <h5 class="mb-0">
                      <i class="mdi mdi-speedometer text-success"></i>
                      {{ point.speed }} Mbps
                    </h5>
                  </div>

                  <div v-if="point.notes" class="mb-3">
                    <label class="text-muted small">Notes</label>
                    <p class="mb-0">{{ point.notes }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Location Info -->
            <div class="col-md-6">
              <div class="card border">
                <div class="card-body">
                  <h6 class="card-title text-primary mb-3">
                    <i class="mdi mdi-map-marker me-1"></i> Location Details
                  </h6>

                  <div class="mb-3">
                    <label class="text-muted small">Region</label>
                    <h5 class="mb-0">{{ point.region }}</h5>
                  </div>

                  <div class="mb-3">
                    <label class="text-muted small">Coordinates</label>
                    <p class="mb-0">
                                            <span class="badge bg-light text-dark">
                                                <i class="mdi mdi-latitude"></i> {{ point.latitude }}
                                            </span>
                      <span class="badge bg-light text-dark ms-2">
                                                <i class="mdi mdi-longitude"></i> {{ point.longitude }}
                                            </span>
                    </p>
                  </div>

                  <div class="mb-3">
                    <button
                        @click="openInGoogleMaps"
                        class="btn btn-sm btn-outline-primary"
                    >
                      <i class="mdi mdi-google-maps me-1"></i> View in Google Maps
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Meta Info -->
            <div class="col-12 mt-3">
              <div class="card border bg-light">
                <div class="card-body py-2">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <small class="text-muted">
                        <i class="mdi mdi-account me-1"></i>
                        Added by: <strong>{{ point.addedBy }}</strong>
                      </small>
                    </div>
                    <div class="col-md-6 text-md-end">
                      <small class="text-muted">
                        <i class="mdi mdi-calendar me-1"></i>
                        {{ formatDate(point.dateAdded) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Speed Test Results (if available) -->
          <div class="row mt-3" v-if="speedTestResults">
            <div class="col-12">
              <div class="card border">
                <div class="card-body">
                  <h6 class="card-title text-primary mb-3">
                    <i class="mdi mdi-speedometer-slow me-1"></i> Latest Speed Test Results
                  </h6>
                  <div class="row text-center">
                    <div class="col-4">
                      <h4 class="text-success">{{ speedTestResults.download }} Mbps</h4>
                      <small class="text-muted">Download</small>
                    </div>
                    <div class="col-4">
                      <h4 class="text-info">{{ speedTestResults.upload }} Mbps</h4>
                      <small class="text-muted">Upload</small>
                    </div>
                    <div class="col-4">
                      <h4 class="text-warning">{{ speedTestResults.ping }} ms</h4>
                      <small class="text-muted">Ping</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i> Close
          </button>
          <button type="button" class="btn btn-primary" @click="$emit('edit', point)">
            <i class="mdi mdi-pencil me-1"></i> Edit
          </button>
          <button type="button" class="btn btn-danger" @click="$emit('delete', point)">
            <i class="mdi mdi-delete me-1"></i> Delete
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'InternetPointDetailsModal',
  props: {
    point: {
      type: Object,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      speedTestResults: null // This could be loaded from API if you track speed tests
    }
  },
  methods: {
    formatDate(date) {
      return moment(date).format('MMMM Do YYYY, h:mm a');
    },

    getConnectionIcon(type) {
      const icons = {
        'Fiber': 'assets/images/map-assets/internet/fiber-icon.png',
        '4G': 'assets/images/map-assets/internet/4g-icon.png',
        '5G': 'assets/images/map-assets/internet/5g-icon.png',
        'Wireless': 'assets/images/map-assets/internet/wifi-icon.png',
        'Satellite': 'assets/images/map-assets/internet/satellite-icon.png'
      };
      return icons[type] || icons['Wireless'];
    },

    openInGoogleMaps() {
      const url = `https://www.google.com/maps?q=${this.point.latitude},${this.point.longitude}`;
      window.open(url, '_blank');
    }
  }
}
</script>

<style scoped>
.modal.show {
  display: block;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
</style>